import { useContext, useEffect } from 'react';
import { SessionContext } from '../../context/SessionContext';
import { useCurrentSession } from '../chat/useCurrentSession';
import { Operation, messages, validateMessage } from './messagesUtils';

interface UseChildProps {
  standalone?: boolean;
  createSession: () => void;
}

interface UseChildResult {
  allowed: boolean;
}

export const useChild = ({ standalone, createSession }: UseChildProps): UseChildResult => {
  const { selectSession } = useContext(SessionContext);
  const { sessionId } = useCurrentSession();

  useEffect(() => {
    if (standalone) {
      const handler = (e: MessageEvent) => {
        const message = validateMessage(e);

        if (message) {
          switch (message.op) {
            case Operation.selectSession:
              if (message.sessionId) {
                selectSession(message.sessionId);
              } else if (!sessionId) {
                createSession();
              }
              break;
            default:
          }
        }
      };

      window.addEventListener('message', handler);

      return () => {
        window.removeEventListener('message', handler);
      };
    }

    return () => {};
  }, [standalone, sessionId, createSession]);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    const closeHandler = () => {
      clearInterval(timer);
      window.opener?.postMessage(messages.childIsClosed(sessionId));
    };

    if (standalone) {
      if (window.opener) {
        timer = setInterval(() => {
          if (!window.opener) {
            window.close();
          } else {
            window.opener.postMessage(messages.pingParent());
          }
        }, 1000);
      } else {
        window.close();
      }

      window.addEventListener('beforeunload', closeHandler);
    }

    return () => {
      clearInterval(timer);
      window.removeEventListener('beforeunload', closeHandler);
    };
  }, [standalone, sessionId]);

  return {
    allowed: !standalone || !!window.opener,
  };
};
